import { encodeField } from '@emdzej/ncsx-cabd';
/**
 * Build slots from a FunctionList + a map of FSW-keyword → value.
 *
 * The FunctionList comes from `buildFunctionList(cabd, …)` in
 * `@emdzej/ncsx-function-list`. We need it (not just the raw CABD)
 * because:
 *   - It pre-resolves FSW IDs → keyword strings via SWT.
 *   - It unifies `PARZUWEISUNG_FSW` and `PARZUWEISUNG_DIR` into one
 *     iterable list — the caller doesn't have to know which block
 *     a given FSW lives in.
 *   - It pre-parses OPERATION/EINHEIT/MASKE for the cabd-encoder
 *     consumption.
 */
export function buildSlotsFromValues(functionList, opts) {
    const byKeyword = indexItemsByKeyword(functionList);
    const nettoSize = computeNettoSize(functionList);
    const netto = opts.initialNetto && opts.initialNetto.length >= nettoSize
        ? Uint8Array.from(opts.initialNetto)
        : padTo(opts.initialNetto, nettoSize);
    const touchedAddrs = new Set();
    const applied = [];
    const skipped = [];
    for (const [keyword, value] of opts.values) {
        const item = byKeyword.get(keyword);
        if (!item) {
            skipped.push({ keyword, reason: `FSW keyword not in CABD` });
            continue;
        }
        try {
            const addresses = applyOne(item, value, netto);
            for (const addr of addresses)
                touchedAddrs.add(addr);
            applied.push({ keyword, addresses });
        }
        catch (err) {
            skipped.push({
                keyword,
                reason: err instanceof Error ? err.message : String(err),
            });
        }
    }
    const slots = [...touchedAddrs]
        .sort((a, b) => a - b)
        .map((addr) => ({ addr, value: netto[addr] }));
    return { slots, netto, applied, skipped };
}
// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────
function indexItemsByKeyword(list) {
    const out = new Map();
    for (const item of list.items) {
        if (item.kind === 'property' || item.kind === 'function') {
            if (item.fswKeyword)
                out.set(item.fswKeyword, item);
        }
    }
    return out;
}
/**
 * Apply a single (keyword, value) edit into the netto. Returns the
 * list of addresses touched (so the caller can dedupe across edits
 * that target overlapping bytes — e.g. high/low nibbles of the same
 * byte).
 */
function applyOne(item, value, netto) {
    if (item.kind === 'property') {
        return applyProperty(item, value, netto);
    }
    return applyFunction(item, value, netto);
}
function applyProperty(item, value, netto) {
    const numeric = coercePropertyValue(value, item.unit);
    const rule = propertyToRule(item);
    encodeField(rule, numeric, netto);
    return range(item.address, item.length);
}
function applyFunction(item, value, netto) {
    if (typeof value !== 'string') {
        throw new Error(`FunctionItem "${item.fswKeyword}" expects a PSW keyword string, got ${typeof value}`);
    }
    const param = item.parameters.find((p) => p.pswKeyword === value);
    if (!param) {
        const known = item.parameters.map((p) => p.pswKeyword).filter(Boolean).join(', ') || '(none)';
        throw new Error(`unknown PSW "${value}" for FSW "${item.fswKeyword}" — known PSWs: ${known}`);
    }
    // The Parameter.data bytes are already MASKE-aligned for the
    // FSW's slot — OR them into the netto positions covered by the
    // item's mask. Same semantics as encodeField's final write step.
    for (let i = 0; i < item.length; i++) {
        const mask = item.mask[i] & 0xff;
        const src = param.data[i] ?? 0;
        netto[item.address + i] = (netto[item.address + i] & ~mask) | (src & mask);
    }
    return range(item.address, item.length);
}
/**
 * Coerce a user-supplied value (`string | number`) into the numeric
 * form `encodeField` expects, based on the item's EINHEIT.
 *
 *   - `'a'` ASCII byte: 1-char string → `charCodeAt(0)`. Numeric
 *     input also accepted (already a code-point).
 *   - `'h'` raw hex byte / `'A'` hex digit / `'d'` decimal:
 *     `parseInt` for strings.
 *   - `'b'` binary string: passed through as `parseInt(s, 2)`.
 *
 * Anything not recognisable throws — caller's `skipped` list
 * surfaces the issue.
 */
function coercePropertyValue(value, unit) {
    if (typeof value === 'number')
        return value >>> 0;
    const s = value.trim();
    switch (unit) {
        case 'a':
            if (s.length !== 1) {
                throw new Error(`EINHEIT='a' expects a 1-char string, got "${s}"`);
            }
            return s.charCodeAt(0) & 0xff;
        case 'h':
            return Number.parseInt(s, 16) >>> 0;
        case 'A':
            // Single hex digit → nibble value. encodeField's autoShift handles the placement.
            if (s.length !== 1) {
                throw new Error(`EINHEIT='A' expects a 1-char hex digit, got "${s}"`);
            }
            return Number.parseInt(s, 16) & 0xf;
        case 'd':
            return Number.parseInt(s, 10) >>> 0;
        case 'b':
            return Number.parseInt(s, 2) >>> 0;
        default:
            throw new Error(`unsupported EINHEIT="${unit}"`);
    }
}
/**
 * Convert a FunctionList PropertyItem (the "rich tree" form) back
 * into a CabdRule (the "encodeField input" form). The two carry the
 * same information; the function-list builder already parsed the
 * raw bytes into structured fields, but encodeField wants the
 * Operation[] decoded from the per-op 5-byte Uint8Array entries.
 */
function propertyToRule(item) {
    const operations = [];
    for (const opBytes of item.operations) {
        const opChar = String.fromCharCode(opBytes[0] ?? 0);
        if (opChar === '!') {
            operations.push({ op: '!' });
            continue;
        }
        if (isOperandOp(opChar)) {
            // u32 LE at bytes[1..5]
            const operand = (opBytes[1] ?? 0) |
                ((opBytes[2] ?? 0) << 8) |
                ((opBytes[3] ?? 0) << 16) |
                ((opBytes[4] ?? 0) << 24);
            operations.push({ op: opChar, operand: operand >>> 0 });
        }
        // Unknown op-chars silently skipped — encodeField would also
        // ignore them. Real-world CABDs only use the canonical set.
    }
    return {
        wortadr: item.address,
        byteadr: item.length,
        maske: Array.from(item.mask),
        einheit: item.unit || 'h',
        operations,
    };
}
function isOperandOp(c) {
    return c === '&' || c === '*' || c === '+' || c === '-' || c === '/' || c === '>' || c === '^' || c === '|';
}
/**
 * Find the smallest power-of-two netto size that fits every item's
 * `address + length`. Mirrors `computeNettoSize` in `rule.ts` but
 * operates over the FunctionList instead of a single block.
 */
function computeNettoSize(list) {
    let max = 0;
    for (const item of list.items) {
        if (!hasAddress(item))
            continue;
        const end = item.address + item.length;
        if (end > max)
            max = end;
    }
    return max;
}
function hasAddress(item) {
    return item.kind === 'property' || item.kind === 'function';
}
function range(start, length) {
    const out = [];
    for (let i = 0; i < length; i++)
        out.push(start + i);
    return out;
}
function padTo(src, minSize) {
    const out = new Uint8Array(Math.max(minSize, src?.length ?? 0));
    if (src)
        out.set(src);
    return out;
}
//# sourceMappingURL=slot-builder.js.map